<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <!-- Header Section -->
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Refund</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>

            <!-- Advanced Search Filter Panel -->
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Advanced Search</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateRefundOn"
                            :from-label="formLabelText.dateRefundOnFrom"
                            :to-label="formLabelText.dateRefundOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.refundId"
                                    label="Refund ID"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.orderNumber"
                                    :label="formLabelText.orderNumber"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.marketplaceCode"
                                    :items="marketplaceOptions"
                                    item-title="code"
                                    item-value="code"
                                    :label="formLabelText.marketplaceCode"
                                    clearable
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.refundType"
                                    :items="refundTypeOptions"
                                    item-title="value"
                                    item-value="value"
                                    :label="formLabelText.refundType"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-select
                                    v-model="data.searchFilter.balanceType"
                                    :items="balanceTypeOptions"
                                    item-title="value"
                                    item-value="value"
                                    :label="formLabelText.balanceType"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                    v-model="data.searchFilter.description"
                                    :label="formLabelText.description"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="6">
                                <v-text-field
                                    v-model="data.searchFilter.reason"
                                    :label="formLabelText.reason"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                                <v-text-field
                                    v-model="data.searchFilter.remarks"
                                    :label="formLabelText.remarks"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Alert Messages -->
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <alert-message ref="paginationErrorAlert" :message="errorMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this refund?
                        <br /><br />
                        <strong>Refund ID:</strong> {{ data.deleteOperation.selectedRefund?.refundId || '' }}
                        <br />
                        <strong>Amount:</strong> {{ data.deleteOperation.selectedRefund?.refundAmount || '' }}
                        <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Action Buttons and Table -->
            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn color="success" @click="onImportClick"> Create by import </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="data.csvExportConfig.filename"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>

                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'refundId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more refunds...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteRefundClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { RefundLabelTextHelper } from '../../utils/labelTextHelper/RefundLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import { RefundHeaderHelper } from '../../utils/headerHelper/RefundHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useEnums } from '../../composables/useEnums';
import { useMasterData } from '../../composables/useMasterData';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { RefundFilterDefinitions } from '../../utils/filterDefinitions/RefundFilterDefinitions';

export default defineComponent({
    name: 'ListRefund',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        // Load enum options for dropdowns
        const { refundTypeOptions, balanceTypeOptions, loadAllEnums } = useEnums();

        // Load master data options for dropdowns
        const { marketplaceOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateRefundOn: {
                    from: null,
                    to: null
                }
            },
            searchFilterDefault: {},
            searchFilter: {
                refundId: null,
                orderNumber: null,
                marketplaceCode: null,
                refundType: null,
                balanceType: null,
                description: null,
                reason: null,
                remarks: null
            },
            csvExportConfig: {
                filename: 'exportRefund.csv',
                csvHeader: [...RefundHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedRefund: null
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: RefundFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    } else {
                        data.searchFilter[key] = filters[key];
                    }
                });
            },
            validationContext: {
                marketplaceOptions,
                refundTypeOptions,
                balanceTypeOptions
            }
        });

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            initialize
        } = usePagination({
            apiEndpoint: '/api/staff/refund/search',
            buildApiParams: (page: number) => {
                const params: any = {};

                // Add date range filtering
                if (data.searchFilterDateRangeGrp.dateRefundOn.from) {
                    params.dateRefundOnFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateRefundOn.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateRefundOn.to) {
                    params.dateRefundOnTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateRefundOn.to
                    );
                }

                // Add refund ID filter
                if (data.searchFilter.refundId?.trim()) {
                    params.refundId = data.searchFilter.refundId.trim();
                }

                // Add text filters
                if (data.searchFilter.orderNumber?.trim()) {
                    params.orderNumber = data.searchFilter.orderNumber.trim();
                }

                if (data.searchFilter.marketplaceCode) {
                    params.marketplaceCode = data.searchFilter.marketplaceCode;
                }

                if (data.searchFilter.refundType) {
                    params.refundType = data.searchFilter.refundType;
                }

                if (data.searchFilter.balanceType) {
                    params.balanceType = data.searchFilter.balanceType;
                }

                if (data.searchFilter.description?.trim()) {
                    params.description = data.searchFilter.description.trim();
                }

                if (data.searchFilter.reason?.trim()) {
                    params.reason = data.searchFilter.reason.trim();
                }

                if (data.searchFilter.remarks?.trim()) {
                    params.remarks = data.searchFilter.remarks.trim();
                }

                return params;
            }
        });

        onMounted(async () => {
            // Load enum options and master data FIRST (for dropdown validation)
            await Promise.all([loadAllEnums(), loadAllMasterData()]);

            // Clone defaults BEFORE restoring from URL
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);
            data.searchFilterDefault = _.cloneDeep(data.searchFilter);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: RefundLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: RefundHeaderHelper.getActionHeaderItem(),
            headers: [...RefundHeaderHelper.getHeaders()],
            courierCodeList: [],
            data,
            // Enum options for dropdowns
            refundTypeOptions,
            balanceTypeOptions,
            // Master data options for dropdowns
            marketplaceOptions,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            // URL Filters
            syncFiltersToUrl
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);
            this.data.searchFilter = _.cloneDeep(this.data.searchFilterDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.$refs.errorAlert.showAlert();
                return;
            }

            // Validate: orderNumber requires marketplaceCode
            if (this.data.searchFilter.orderNumber?.trim() && !this.data.searchFilter.marketplaceCode) {
                this.data.errorAlertMessage = 'Marketplace Code is required when filtering by Order Number';
                this.$refs.errorAlert.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                ...this.data.searchFilter,
                dateRefundOnFrom: this.data.searchFilterDateRangeGrp.dateRefundOn.from,
                dateRefundOnTo: this.data.searchFilterDateRangeGrp.dateRefundOn.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onImportClick() {
            await this.$router.push({ path: PathDefinition.importRefund });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onPageSizeChange() {
            this.changePageSize();
        },
        onPageSelect(page: number) {
            this.selectPage(page);
        },
        onNextPage() {
            this.nextPage();
        },
        onPreviousPage() {
            this.previousPage();
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },

        onDeleteRefundClick(item: any) {
            this.data.deleteOperation.selectedRefund = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },

        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedRefund = null;
        },

        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedRefund) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const refundId = this.data.deleteOperation.selectedRefund.refundId;
                const requestUrl = `/api/staff/refund/batchDelete`;
                const requestData = {
                    refundIdList: [refundId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(refundId, 'refundId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedRefund = null;
                    },
                    'Failed to delete refund'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the refund');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>
